import { ListItem, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { parseDiagnostic } from './diagnostic';

interface Props {
  line: string;
}

/**
 * One problem the renderer found with a template.
 *
 * The code and the position are lifted out of the sentence into a header, since
 * an author reading a list of these is looking for where to go rather than
 * reading each one through. The rule down the left is the marker an editor puts
 * in its gutter, which is where the same author meets the same information
 * everywhere else.
 *
 * Code and position are set in a monospace face because they are an identifier
 * and a coordinate. The message keeps the body face: it is a sentence, and only
 * happens to carry an identifier inside it.
 */
export const DiagnosticItem = ({ line }: Props): React.JSX.Element => {
  const { code, position, message } = useMemo(() => parseDiagnostic(line), [line]);

  return (
    <ListItem
      sx={{
        display: 'block',
        borderLeft: 2,
        borderColor: 'error.main',
        py: 1.25,
        pl: 2,
        pr: 2,
      }}
    >
      {code !== undefined && (
        <Typography
          component={'div'}
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.02em',
            mb: 0.5,
          }}
        >
          <Typography component={'span'} color={'error.main'} sx={{ font: 'inherit', fontWeight: 700 }}>
            {code}
          </Typography>
          {position !== undefined && (
            <Typography component={'span'} color={'text.secondary'} sx={{ font: 'inherit', ml: 1.5 }}>
              {position}
            </Typography>
          )}
        </Typography>
      )}
      <Typography
        component={'div'}
        variant={'body2'}
        sx={{
          // An identifier such as `Q{java:java.lang.Runtime}getRuntime()` has
          // nowhere to break, so it is allowed to break anywhere rather than
          // widen the card.
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
        }}
      >
        {message}
      </Typography>
    </ListItem>
  );
};
